import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, errorText } from '../lib/api'
import { keys, useProjects } from '../lib/queries'
import { formatRelative } from '../lib/format'
import { DEFAULT_SCOPES, SCOPES, type ApiToken, type Scope } from '../lib/types'
import { Button, Field, Input, Section, cx, useToast } from '../components/ui'

/**
 * Connecting an AI agent.
 *
 * The hard part of MCP is not the protocol, it is that somebody has to paste
 * the right command into the right place while being sure they are not handing
 * a robot the keys to everything. So this screen is built around the two
 * decisions that actually matter — what may it do, and where — and then hands
 * back a command that can be copied without editing.
 *
 * The token is shown once. It never expires, because the thing holding it is a
 * configuration file nobody is watching, and a token that stops working at 3am
 * fails in the least useful way there is. Revoking is one click.
 */
export function AgentSection() {
  const tokens = useQuery({ queryKey: keys.tokens(), queryFn: api.tokens })
  const projects = useProjects()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<Scope[]>(DEFAULT_SCOPES)
  const [limitProjects, setLimitProjects] = useState(false)
  const [chosen, setChosen] = useState<string[]>([])
  const [issued, setIssued] = useState<{ secret: string; url: string; token: ApiToken } | null>(null)

  const mcpUrl = `${window.location.origin}/mcp`

  const create = useMutation({
    mutationFn: () => api.createToken(name.trim(), scopes, limitProjects ? chosen : []),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: keys.tokens() })
      setIssued({ secret: data.secret, url: data.mcpUrl || mcpUrl, token: data.token })
      setName('')
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteToken(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.tokens() }),
    onError: (e) => toast.show('Could not revoke', { tone: 'error', detail: errorText(e) }),
  })

  const error = create.error instanceof ApiError ? create.error : null

  function toggleScope(id: Scope) {
    setScopes((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    )
  }

  function copy(value: string, what: string) {
    navigator.clipboard?.writeText(value)
    toast.show(`${what} copied`)
  }

  return (
    <Section
      title="AI agents and API tokens"
      hint="Give an AI agent its own connection to this board over MCP, or a script the same token for the REST API. It acts as you, limited to what you tick below, and everything it does is recorded under your name."
    >
      {issued ? (
        <IssuedToken
          secret={issued.secret}
          url={issued.url}
          token={issued.token}
          onCopy={copy}
          onDone={() => setIssued(null)}
        />
      ) : (
        <>
          <div className="rounded-md border border-[var(--line)] bg-[var(--sunken)] px-3 py-2.5">
            <p className="text-[12.5px] text-[var(--ink-2)]">
              This installation speaks MCP at
            </p>
            <div className="mt-1 flex items-center gap-2">
              <code className="task-key flex-1 truncate rounded border border-[var(--line)] bg-[var(--surface)] px-2 py-1 text-[12px]">
                {mcpUrl}
              </code>
              <Button size="sm" onClick={() => copy(mcpUrl, 'Address')}>
                Copy
              </Button>
            </div>
            <p className="mt-2 text-[12px] text-[var(--ink-3)]">
              Create a token below and you will get the full command to run.
            </p>
          </div>

          <div className="mt-4 flex items-end gap-2">
            <div className="flex-1">
              <Field
                label="What is connecting?" htmlFor="agent-name" error={error?.fields?.name}
                hint="A name you will recognise in the list below, like “claude on my laptop”."
              >
                <Input
                  id="agent-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="claude code"
                />
              </Field>
            </div>
            <Button
              variant="primary"
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={!name.trim() || scopes.length === 0}
            >
              Create token
            </Button>
          </div>

          <fieldset className="mt-4">
            <legend className="text-[12px] font-medium text-[var(--ink-2)]">
              What may it do?
            </legend>
            {error?.fields?.scopes && (
              <p className="mt-1 text-[12px] text-[var(--danger)]">{error.fields.scopes}</p>
            )}
            <div className="mt-2 space-y-1">
              {SCOPES.map((s) => (
                <label
                  key={s.id}
                  className={cx(
                    'flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-[12.5px]',
                    'hover:bg-[var(--sunken)]',
                    s.danger && 'text-[var(--danger)]',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(s.id)}
                    onChange={() => toggleScope(s.id)}
                    className="mt-0.5 accent-[var(--accent)]"
                  />
                  <span>{s.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[12px] text-[var(--ink-3)]">
              An agent is never offered a tool it was not granted. Deleting is not available
              over MCP at all, whatever is ticked here — archiving is the version it gets.
            </p>
          </fieldset>

          <fieldset className="mt-4">
            <label className="flex cursor-pointer items-center gap-2 text-[12px] font-medium text-[var(--ink-2)]">
              <input
                type="checkbox"
                checked={limitProjects}
                onChange={(e) => setLimitProjects(e.target.checked)}
                className="accent-[var(--accent)]"
              />
              Limit it to certain projects
            </label>
            {error?.fields?.projectIds && (
              <p className="mt-1 text-[12px] text-[var(--danger)]">{error.fields.projectIds}</p>
            )}
            {limitProjects && (
              <>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(projects.data ?? [])
                    .filter((p) => !p.parentId)
                    .map((p) => (
                      <button
                        key={p.id}
                        onClick={() =>
                          setChosen((c) =>
                            c.includes(p.id) ? c.filter((id) => id !== p.id) : [...c, p.id],
                          )
                        }
                        aria-pressed={chosen.includes(p.id)}
                        className={cx(
                          'rounded border px-2 py-0.5 text-[12px] transition-colors duration-[140ms]',
                          chosen.includes(p.id)
                            ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                            : 'border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--line-strong)]',
                        )}
                      >
                        <span className="task-key">{p.key}</span> {p.name}
                      </button>
                    ))}
                </div>
                <p className="mt-1.5 text-[12px] text-[var(--ink-3)]">
                  Picking a project includes its sub-projects, the same way membership does.
                  Leave everything unticked and the token reaches nothing.
                </p>
              </>
            )}
          </fieldset>
        </>
      )}

      <div className="mt-5 space-y-1.5">
        {tokens.data?.length === 0 && (
          <p className="text-[12.5px] text-[var(--ink-3)]">
            Nothing is connected yet.
          </p>
        )}
        {tokens.data?.map((t) => (
          <div
            key={t.id}
            className="rounded-md border border-[var(--line)] px-2.5 py-1.5 text-[12.5px]"
          >
            <div className="flex items-center gap-3">
              <span className="font-medium">{t.name}</span>
              <span className="task-key text-[11px] text-[var(--ink-3)]">{t.prefix}…</span>
              <span className="ml-auto text-[11px] text-[var(--ink-3)]">
                {t.lastUsedAt ? `used ${formatRelative(t.lastUsedAt)}` : 'never used'}
              </span>
              <button
                onClick={() => remove.mutate(t.id)}
                className="text-[11px] text-[var(--ink-3)] hover:text-[var(--danger)]"
              >
                Revoke
              </button>
            </div>
            <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              {describeScopes(t.scopes)}
              {t.projects && t.projects.length > 0
                ? ` · only ${t.projects.map((p) => p.key).join(', ')}`
                : ''}
            </p>
          </div>
        ))}
      </div>
    </Section>
  )
}

/**
 * The one moment the secret exists on screen.
 *
 * It shows the finished command rather than the bare token, because the token
 * on its own leaves somebody to work out the rest, and that is where people
 * give up or paste it somewhere it should not go.
 */
function IssuedToken({
  secret, url, token, onCopy, onDone,
}: {
  secret: string
  url: string
  token: ApiToken
  onCopy: (value: string, what: string) => void
  onDone: () => void
}) {
  const command = `claude mcp add --transport http artsol-tasks ${url} \\\n  --header "Authorization: Bearer ${secret}"`

  const instructions = [
    'When you work on a task from artsol tasks:',
    '- Read it with get_task before starting, including its checklist.',
    '- Tick each checklist item off with set_subtask_done as you finish it.',
    '- After the change is pushed, comment on the task saying what landed.',
    '- Ask me before moving a task to Done.',
    '- If your change needs work from another team, create a task in their project.',
  ].join('\n')

  return (
    <div className="rounded-md border border-[var(--accent)] bg-[var(--accent-soft)] p-3">
      <p className="text-[12.5px] font-medium">
        Copy this now — the token is not shown again.
      </p>

      <p className="mt-3 text-[12px] font-medium text-[var(--ink-2)]">
        1. Connect it (Claude Code)
      </p>
      <pre className="mt-1 overflow-x-auto rounded border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5 font-mono text-[11.5px] leading-relaxed">
        {command}
      </pre>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => onCopy(command, 'Command')}>
          Copy command
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onCopy(secret, 'Token')}>
          Copy the token on its own
        </Button>
      </div>
      <p className="mt-1.5 text-[11.5px] text-[var(--ink-2)]">
        For another client, point it at <span className="task-key">{url}</span> over HTTP and
        send the token as an <span className="task-key">Authorization: Bearer</span> header.
      </p>

      <p className="mt-4 text-[12px] font-medium text-[var(--ink-2)]">
        2. Tell it how you want it to work
      </p>
      <p className="mt-0.5 text-[11.5px] text-[var(--ink-2)]">
        Paste this into the agent's own instructions — its CLAUDE.md, system prompt or
        equivalent. Change it to suit; this is a starting point, not a rule.
      </p>
      <pre className="mt-1 overflow-x-auto rounded border border-[var(--line)] bg-[var(--surface)] px-2 py-1.5 font-mono text-[11.5px] leading-relaxed">
        {instructions}
      </pre>
      <Button size="sm" className="mt-1.5" onClick={() => onCopy(instructions, 'Instructions')}>
        Copy instructions
      </Button>

      <p className="mt-4 text-[11.5px] text-[var(--ink-2)]">
        This connection may: {describeScopes(token.scopes)}.
        {token.projects && token.projects.length > 0
          ? ` It reaches only ${token.projects.map((p) => p.key).join(', ')} and their sub-projects.`
          : ' It reaches every project you can see.'}
      </p>

      <Button size="sm" variant="ghost" className="mt-2" onClick={onDone}>
        Done
      </Button>
    </div>
  )
}

/** A short phrase rather than a row of scope names nobody reads. */
function describeScopes(scopes: Scope[] = []): string {
  if (scopes.length === 0) return 'nothing'
  const parts: string[] = []
  if (scopes.includes('read')) parts.push('read')
  if (scopes.includes('write')) parts.push('create and update tasks')
  if (scopes.includes('subtasks')) parts.push('tick subtasks')
  if (scopes.includes('comments')) parts.push('comment')
  if (scopes.includes('archive')) parts.push('archive')
  if (scopes.includes('delete')) parts.push('delete')
  return parts.join(', ')
}
